import { Controller } from '@hotwired/stimulus';
import { acceptedType } from '../js/file_types';

// Envoi de l'image d'une fiche (UiFormBuilder#upload, image) : aperçu de
// l'image choisie (bouton ou glisser-déposer sur le cadre) avant
// l'enregistrement ; type et taille vérifiés dès le choix d'après les règles
// du champ (data-check, mêmes messages que form-check, qui refuse l'envoi
// tant que le fichier reste) ; « Retirer » : abandonne l'image choisie, ou
// passe remove_image à 1 (aperçu vidé, « Annuler le retrait » rend l'image
// enregistrée) ; une nouvelle image annule le retrait.
export default class extends Controller {
    static targets = ['zone', 'preview', 'empty', 'chooseLabel', 'remove', 'restore', 'removeField', 'status'];

    static values = { change: String, removal: String };

    connect() {
        this.saved = this.previewTarget.getAttribute('src');
    }

    disconnect() {
        this.revoke();
    }

    get input() {
        return this.element.querySelector('input[type="file"]');
    }

    pick() {
        const file = this.input.files[0];
        const error = file && this.errorFor(file);
        this.setError(error);
        if (!file || error) {
            this.showSaved();
            if (file) this.removeTarget.hidden = false;
            return;
        }
        this.setRemoval(false);
        this.revoke();
        this.objectUrl = URL.createObjectURL(file);
        this.showPreview(this.objectUrl);
        this.chooseLabelTarget.textContent = this.changeValue;
        this.statusTarget.textContent = `Image choisie : ${file.name}`;
        this.removeTarget.hidden = false;
    }

    errorFor(file) {
        const rules = JSON.parse(this.input.dataset.check || '{}');
        if (rules.accept && !acceptedType(file, rules.accept[0])) return rules.accept[1];
        if (rules.maxsize && file.size > rules.maxsize[0]) return rules.maxsize[1];
        return null;
    }

    setError(message) {
        this.input.setAttribute('aria-invalid', message ? 'true' : 'false');
        const error = document.getElementById(`${this.input.id}_error`);
        if (!error) return;
        error.textContent = message || '';
        error.hidden = !message;
    }

    // Image choisie abandonnée ; sinon image enregistrée retirée à
    // l'enregistrement.
    remove() {
        const chosen = this.input.files.length > 0;
        this.input.value = '';
        this.setError(null);
        if (chosen) {
            this.showSaved();
            (this.saved && !this.removing ? this.removeTarget : this.input).focus();
            return;
        }
        this.setRemoval(true);
        this.showPreview(null);
        this.statusTarget.textContent = this.removalValue;
        this.removeTarget.hidden = true;
        this.restoreTarget.hidden = false;
        this.restoreTarget.focus();
    }

    restore() {
        this.setRemoval(false);
        this.showSaved();
        this.removeTarget.focus();
    }

    setRemoval(removing) {
        if (this.hasRemoveFieldTarget) this.removeFieldTarget.value = removing ? '1' : '0';
        if (!removing) this.restoreTarget.hidden = true;
    }

    // Image enregistrée (ou aucune, ou retrait annoncé), image choisie oubliée.
    showSaved() {
        this.revoke();
        this.showPreview(this.removing ? null : this.saved);
        this.statusTarget.textContent = this.removing ? this.removalValue : '';
        this.removeTarget.hidden = !this.saved || this.removing;
    }

    get removing() {
        return this.hasRemoveFieldTarget && this.removeFieldTarget.value === '1';
    }

    showPreview(src) {
        this.previewTarget.hidden = !src;
        this.emptyTarget.hidden = Boolean(src);
        if (src) this.previewTarget.src = src;
        else this.previewTarget.removeAttribute('src');
    }

    over(event) {
        event.preventDefault();
        this.zoneTarget.dataset.over = '';
    }

    leave(event) {
        if (!this.zoneTarget.contains(event.relatedTarget)) delete this.zoneTarget.dataset.over;
    }

    drop(event) {
        event.preventDefault();
        delete this.zoneTarget.dataset.over;
        const file = event.dataTransfer?.files?.[0];
        if (!file) return;
        const transfer = new DataTransfer();
        transfer.items.add(file);
        this.input.files = transfer.files;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    revoke() {
        if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = null;
    }
}
